import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router";
import { applyLanguage } from "@repo/i18n";
import { deriveShellState, useVenueConfig } from "../config/useVenueConfig";
import { resolveChannel, resolvePreview } from "../channel";
import { resolveVenueLanguage } from "../i18n/diner-language";
import { ShellFrame } from "../shell/ShellFrame";
import { ErrorState, LoadingState, NotFoundState, UnavailableState } from "../shell/states";
import { BookingFlowOutlet, toBookingFlowContext } from "./BookingFlowOutlet";

/** True when this document is framed (an embed/island), false for a bare standalone visit. */
function isFramed(): boolean {
  return typeof window !== "undefined" && window.parent !== window;
}

/**
 * `/v/:venueSlug` — the standalone booking page AND the iframe/island target. Resolves the public
 * venue config, maps it to exactly one fail-safe shell state, applies the venue's default language
 * once (an explicit diner choice always wins), and hands the happy path to the booking outlet with
 * the resolved channel (spec R3). One venue per mount — no venue switcher (R5).
 */
export function BookingPage() {
  const { venueSlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const query = useVenueConfig(venueSlug);
  const config = query.data;

  const framed = isFramed();
  const channel = resolveChannel(searchParams.get("channel"), framed ? "iframe" : "standalone");
  const preview = resolvePreview(searchParams.get("preview"));
  const langParam = searchParams.get("lang");

  // Page-level "standalone" styling (grey backdrop + centered card + padding ON <body>) may apply ONLY
  // when the widget owns the whole document — a bare standalone visit to /v/:slug. The storefront island
  // is also not framed, but it mounts INLINE inside the venue site, so it must never restyle the host
  // <body> (that leaked a grey background + page padding onto the storefront). The island is the one
  // non-framed mount carrying the subdomain channel, so exclude it.
  const ownsPage = !framed && channel !== "subdomain";
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.body.classList.toggle("rsv-standalone", ownsPage);
    return () => document.body.classList.remove("rsv-standalone");
  }, [ownsPage]);

  // Once the venue default is known, resolve the diner language against it (explicit choice wins).
  const venueDefault = config?.defaultLanguage;
  useEffect(() => {
    if (!venueDefault) return;
    const browser = typeof navigator !== "undefined" ? navigator.languages : [];
    void applyLanguage(resolveVenueLanguage(langParam, browser ?? [], venueDefault));
  }, [venueDefault, langParam]);

  const state = deriveShellState({
    isLoading: query.isLoading,
    isError: query.isError,
    data: config,
  });

  const body = (() => {
    switch (state) {
      case "loading":
        return <LoadingState />;
      case "error":
        return <ErrorState onRetry={() => void query.refetch()} />;
      case "not-found":
        return <NotFoundState />;
      case "unavailable":
        return <UnavailableState config={config} />;
      case "ready":
        return (
          <BookingFlowOutlet context={toBookingFlowContext(venueSlug, config!, channel, preview)} />
        );
    }
  })();

  return (
    <ShellFrame venueName={config?.venueName} logoUrl={config?.logoUrl} branding={config?.branding}>
      {body}
    </ShellFrame>
  );
}
